/**
 * API fetch helper – adds JWT when available for protected routes.
 *
 * If you see "Missing or invalid JWT token" on send-otp or verify-otp:
 * The backend must exclude these from JWT auth. In Spring Security:
 *   .requestMatchers("/api/auth/send-otp", "/api/auth/verify-mobile-otp", "/api/auth/create-account").permitAll()
 */
const TOKEN_KEY = 'token';
/** Some backends expect "JWT " instead of "Bearer " - set REACT_APP_AUTH_PREFIX=JWT in .env */
const AUTH_PREFIX = (process.env.REACT_APP_AUTH_PREFIX || 'Bearer').trim();

export const AUTH_EXPIRED_EVENT = 'daiv:auth-expired';

/** Notify AuthContext to clear React state when token is cleared (401 / invalid session). */
export function emitAuthExpired() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  }
}

/** Check if JWT is expired. Returns true if no token or expired. Use to treat user as guest. */
export function isTokenExpired() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return true;
    const payload = JSON.parse(atob(token.split('.')[1] || '{}'));
    const exp = payload?.exp;
    if (!exp) return false; // no exp claim – assume valid
    return Date.now() >= exp * 1000; // exp is in seconds
  } catch {
    return true;
  }
}

export function getAuthHeaders(includeToken = true) {
  const headers = { 'Content-Type': 'application/json' };
  if (includeToken) {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      headers.Authorization = `${AUTH_PREFIX} ${token}`.trim();
    }
  }
  return headers;
}

/**
 * Human-readable message from typical API error JSON (Spring, Nest, Express, etc.).
 * Use when `res.ok` is false so users see the server message instead of a generic string.
 */
export function getApiErrorMessage(json, fallback = 'Something went wrong') {
  if (json == null || typeof json !== 'object') return fallback;
  const msg = json.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (Array.isArray(msg) && msg.length) {
    const first = msg.find((x) => typeof x === 'string' && x.trim());
    if (first) return first.trim();
  }
  const err = json.error;
  if (typeof err === 'string' && err.trim()) return err.trim();
  if (err && typeof err === 'object' && typeof err.message === 'string' && err.message.trim()) {
    return err.message.trim();
  }
  const detail = json.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) {
    const d0 = detail[0];
    if (typeof d0 === 'string' && d0.trim()) return d0.trim();
    if (d0 && typeof d0.msg === 'string' && d0.msg.trim()) return d0.msg.trim();
  }
  const dataMsg = json.data?.message ?? json.data?.error;
  if (typeof dataMsg === 'string' && dataMsg.trim()) return dataMsg.trim();
  const errors = json.errors;
  if (Array.isArray(errors) && errors.length) {
    const e0 = errors[0];
    if (typeof e0 === 'string' && e0.trim()) return e0.trim();
    if (e0 && typeof e0.message === 'string' && e0.message.trim()) return e0.message.trim();
    if (e0 && typeof e0.msg === 'string' && e0.msg.trim()) return e0.msg.trim();
  }
  if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
    const keys = Object.keys(errors);
    if (keys.length) {
      const k = keys[0];
      const v = errors[k];
      if (typeof v === 'string' && v.trim()) return `${k}: ${v.trim()}`;
      if (Array.isArray(v) && v.length && typeof v[0] === 'string') return v[0].trim();
    }
  }
  return fallback;
}

/**
 * Public (no JWT) routes — pathname-based so we never match substrings inside other paths
 * (e.g. `/api/poojas/` must NOT match `/api/poojabookings` or `/api/favorite-poojas`).
 */
function isPublicApiPathname(pathname) {
  if (!pathname || typeof pathname !== 'string') return false;
  const path = pathname.split('?')[0] || pathname;
  if (!path.startsWith('/api/')) return false;
  if (path.startsWith('/api/auth/')) return true;
  if (path.startsWith('/api/contact-us')) return true;
  if (path.startsWith('/api/blog-categories')) return true;
  if (path.startsWith('/api/daivblogs/')) return true;
  if (path.startsWith('/api/deities')) return true;
  if (path.startsWith('/api/kshetras')) return true;
  if (path.startsWith('/api/astro/')) return true;
  if (path.startsWith('/api/divine-moments')) return true;
  if (path.startsWith('/api/nitya-media')) return true;
  if (path.startsWith('/api/public/')) return true;
  if (path.startsWith('/api/event-banners/')) return true;
  if (path.startsWith('/api/feedback')) return true;
  if (path.startsWith('/api/gotras')) return true;
  if (path.startsWith('/api/nakshatras')) return true;
  if (path.startsWith('/api/poojas/')) return true;
  if (path === '/api/poojas') return true;
  /** Live viewer presence — join / heartbeat / leave / count (public). */
  if (path.startsWith('/api/live/')) return true;
  if (path.startsWith('/api/pooja-categories')) return true;
  if (path.startsWith('/api/rashis')) return true;
  if (path.startsWith('/api/visits/')) return true;
  if (path.startsWith('/api/whatsapp-subscribers')) return true;
  if (path.startsWith('/api/gallery')) return true;
  if (path.startsWith('/api/features')) return true;
  if (path.startsWith('/api/coupons/preview') || path === '/api/coupons/preview') return true;
  if (/^\/api\/sevas\/[^/]+$/.test(path)) return true;
  return false;
}

function isPublicApiUrl(urlStr) {
  try {
    const u = new URL(String(urlStr), 'https://placeholder.local');
    return isPublicApiPathname(u.pathname);
  } catch {
    return false;
  }
}

/** Blog list/detail JSON must not use stale HTTP cache or CMS edits won't show until cache expires. */
function isDaivBlogsApiUrl(urlStr) {
  try {
    const u = new URL(String(urlStr), 'https://placeholder.local');
    return u.pathname.startsWith('/api/daivblogs');
  } catch {
    return false;
  }
}

export async function apiFetch(url, options = {}) {
  const { skipAuth, ...fetchOptions } = options;
  const urlStr = String(url);
  const isPublicUrl = isPublicApiUrl(urlStr);
  /** Explicit `skipAuth: false` always sends JWT (e.g. favorites) even if URL were misclassified. */
  const shouldSkipAuth =
    skipAuth === true || (skipAuth !== false && isPublicUrl);
  /* `Content-Type: application/json` on a bodiless GET makes it a non-"simple" CORS request, so the
     browser sends an extra OPTIONS preflight (a full round trip) before every public API call. */
  const reqMethod = String(fetchOptions.method || 'GET').toUpperCase();
  const needsJsonContentType = fetchOptions.body != null || (reqMethod !== 'GET' && reqMethod !== 'HEAD');
  /* Expired token (e.g. Facebook in-app browser reopening an old session): clear it and continue as a
     guest instead of sending a JWT the server will reject. */
  const sendAsGuest = !shouldSkipAuth && Boolean(localStorage.getItem(TOKEN_KEY)) && isTokenExpired();
  if (sendAsGuest) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth_user');
    emitAuthExpired();
  }
  const headers = shouldSkipAuth || sendAsGuest
    ? { ...(needsJsonContentType ? { 'Content-Type': 'application/json' } : {}), ...(options.headers || {}) }
    : { ...getAuthHeaders(true), ...(options.headers || {}) };
  if ((shouldSkipAuth || sendAsGuest) && headers.Authorization) {
    delete headers.Authorization;
  }
  const cache =
    fetchOptions.cache !== undefined
      ? fetchOptions.cache
      : isDaivBlogsApiUrl(urlStr)
        ? 'no-store'
        : undefined;
  const res = await fetch(url, { ...fetchOptions, headers, ...(cache !== undefined ? { cache } : {}) });
  if (res.status === 401 && !shouldSkipAuth) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth_user');
    emitAuthExpired();
  }
  return res;
}
